/**
 * E002-T035: LoggingService (Run-Level Logging)
 * E002-T046: Backend Execution Logs Integration
 *
 * Manages execution logs per RUN (not per row).
 * Based on sl-canvas-stack PIPELINE_LOGGING_AND_VALIDATION.md
 *
 * Structure per Run:
 * - INPUT: Source table, filter config, input mappings, payloads
 * - PROCESS: Workflow execution details, engine steps
 * - OUTPUT: Write results, target table
 *
 * E002-T046: Now also loads logs from backend for trigger executions.
 * Backend logs are merged with frontend in-memory logs.
 */

import { LiveData, Service } from '@toeverything/infra';

import { defaultWorkspaceApiClient } from '../../cloud';
import { workflowWorkspaceRoutes } from './workflow-workspace-routes';

// =============================================================================
// BACKEND LOG TYPES (E002-T046)
// =============================================================================

/** Backend log response from GET /api/workspace/:workspaceId/workflow/logs/:blockId */
export interface BackendLogResponse {
  id: string;
  triggerId: string | null;
  workspaceId: string;
  docId: string;
  blockId: string;
  workflowId: string | null;
  mode: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  error: { step: string; message: string } | null;
  terminalReason?: 'cycle_blocked' | 'suspected_external_loop';
  duration: number | null;
}

/**
 * sl-g3on.9.1: Per-row retry / on-failure entry mirrored from
 * `ProcessStageRowResult` in the backend interface. Frontend cannot
 * import server types — kept structurally byte-compatible.
 */
export interface ProcessStageRowEntry {
  rowId: string;
  /**
   * sl-8fwi.3 rework: `'aborted'` is the new distinct status for
   * rows the runner short-circuited under `onFailure='stop'`.
   * `'abandoned'` remains in the union for backwards-compatible
   * deserialization of pre-rework logs — new logs never write it.
   */
  status: 'success' | 'failed' | 'aborted' | 'abandoned';
  retryCount: number;
  onFailureMode: 'stop' | 'continue';
  error?: string;
  durationMs: number;
}

// =============================================================================
// INPUT STAGE (per Run)
// =============================================================================

export interface InputStageLog {
  /** Source table name */
  sourceTable: string;
  /** Source table ID */
  sourceTableId: string;
  /** Total rows in source before filtering */
  totalRows: number;
  /** Rows that passed the filter */
  passedFilter: number;
  /** Rows that were filtered out */
  filteredOut: number;
  /** Applied filter configuration (human-readable) */
  filterConfig?: Record<string, unknown>;
  /** Selected columns included in payload */
  selectedColumns?: string[];
  /** Aggregated payloads sent to process stage */
  payloads: Array<Record<string, unknown>>;
}

// =============================================================================
// PROCESS STAGE (per Run)
// =============================================================================

export interface WorkflowNodeStep {
  /** Node display name (user-visible, no engine internals) */
  name: string;
  /** Node id from workflow engine / runner progress, when available. */
  nodeId?: string;
  /** Execution status */
  status: 'success' | 'error' | 'running';
  /** Duration in milliseconds */
  duration: number;
  /** Summary of input field names */
  inputSummary?: string[];
  /** Summary of output field names */
  outputSummary?: string[];
  /** sl-j7kp.3.1: Sanitized step input data (field values, not just names). */
  inputData?: Array<Record<string, unknown>>;
  /** sl-j7kp.3.1: Sanitized step output data (field values, not just names). */
  outputData?: Array<Record<string, unknown>>;
  /** Error message if failed */
  error?: string;
  /**
   * sl-oe5x audit M1: Structured error code propagated from the runner
   * (e.g. `STT_PLAN_LIMIT`, `STT_BATCH_FORBIDDEN`). UI resolves to an
   * i18n key via `formatSttBatchError`.
   */
  errorCode?: string;
  /** sl-oe5x audit M1: Template variables for the i18n message. */
  errorDetails?: Record<string, unknown>;
  /** Latest live runner progress for this workflow step. */
  mediaProgress?: RuntimeProgressEvent & { lastUpdatedAt?: number };
}

export interface ProcessStageLog {
  /** Total execution duration in milliseconds */
  duration: number;
  /** Overall status */
  status: 'success' | 'error' | 'running' | 'queued';
  /** Sanitized node execution steps (no infra nodes, no engine data) */
  nodeSteps?: WorkflowNodeStep[];
  /** Error with step context */
  error?: string;
  errorStep?: string;
  /** Last response data from workflow execution */
  response?: Record<string, unknown>;
  /** Execution ID from the workflow engine */
  executionId?: string;
  /** Workflow name for display purposes */
  workflowName?: string;
  /**
   * sl-g3on.9.1: Per-row retry + on-failure metadata. Forwarded from the
   * backend execution log so the panel (sl-g3on.9.2) can render retry
   * badges.
   */
  rows?: ProcessStageRowEntry[];
  runtimeEvents?: RuntimeProgressEvent[];
  mediaProgress?: {
    stepId?: string;
    stepName?: string;
    stepType?: string;
    executionId?: string;
    phase?: 'start' | 'progress' | 'complete';
    activity?: string;
    message?: string;
    percent?: number;
    currentTimeMs?: number;
    totalTimeMs?: number;
    timeRemainingMs?: number;
    bytesDone?: number;
    bytesTotal?: number;
    bitrate?: string;
    fps?: number;
    detail?: Record<string, string | number | boolean>;
    lastUpdatedAt?: number;
  };
}

export interface RuntimeProgressEvent {
  timestamp: number;
  stepId?: string;
  stepName?: string;
  stepType?: string;
  executionId?: string;
  phase?: 'start' | 'progress' | 'complete';
  activity?: string;
  message?: string;
  percent?: number;
  currentTimeMs?: number;
  totalTimeMs?: number;
  timeRemainingMs?: number;
  bytesDone?: number;
  bytesTotal?: number;
  bitrate?: string;
  fps?: number;
  detail?: Record<string, string | number | boolean>;
}

// =============================================================================
// OUTPUT STAGE (per Run)
// =============================================================================

export interface OutputStageLog {
  /** Output mode used */
  mode:
    | 'update_source'
    | 'create_table'
    | 'append_existing'
    | 'update_target'
    | 'not_configured';
  /** Target table name (if applicable) */
  targetTable?: string;
  /** Target table ID (if applicable) */
  targetTableId?: string;
  /** Number of rows written/updated */
  rowsWritten: number;
  /** Number of rows that failed to write */
  rowsFailed: number;
  /** Configuration error (missing settings) */
  error?: string;
  /** Output field mappings (response → target) */
  outputMappings?: Array<{
    responseField: string;
    targetColumn: string;
    transform?: string;
  }>;
  /** Aggregated write results */
  results: Array<{
    rowId: string;
    success: boolean;
    error?: string;
  }>;
}

// =============================================================================
// EXECUTION LOG (per Run)
// =============================================================================

/**
 * sl-67kc.3.3: Long-running operation parked on this execution.
 *
 * Mirrors the backend `LongRunningOpInfo` shape — the contract is an open-
 * enum `kind` so future suspension types (video transcode, bulk AI) can
 * publish without changing this type. Frontend renders a generic
 * `Pending external job` row for unknown kinds.
 */
export interface LongRunningOpInfo {
  suspendedExecutionId: string;
  workflowExecutionId: string;
  rowId: string;
  kind: 'stt';
  status:
    | 'pending'
    | 'resuming'
    | 'completed'
    | 'failed'
    | 'cancelled'
    | 'timeout';
  failureOrigin: 'provider' | 'dispatch' | null;
  /** ISO timestamp. */
  submittedAt: string;
  /** ISO timestamp; 25h provider ceiling. */
  expiresAt: string;
  provider: string;
  model: string;
  dynamicBatching: boolean;
  jobId: string;
  realDurationSeconds: number | null;
  errorMessage: string | null;
  /**
   * sl-67kc.3.3 REWORK: whether `SuspendedExecution.resumePayload` is
   * populated. Drives the Download-Transcript + Retry-from-Cache button
   * visibility in the `LongRunningOpRow` variant. The actual payload
   * ships via `GET /api/stt/jobs/:jobId/transcript`.
   */
  hasCachedResult: boolean;
}

/**
 * Active long-running statuses — Cancel + Delete-guard apply only here.
 * Resuming counts because the resume pod is still in flight (pre-terminal).
 */
const ACTIVE_LRO_STATUSES = new Set<LongRunningOpInfo['status']>([
  'pending',
  'resuming',
]);

export const isLongRunningOpActive = (op: LongRunningOpInfo): boolean =>
  ACTIVE_LRO_STATUSES.has(op.status);

export const hasActiveLongRunningOps = (log: ExecutionLog): boolean =>
  Boolean(log.longRunningOps?.some(isLongRunningOpActive));

export interface ExecutionLog {
  /** Unique log ID */
  id: string;
  /** Block this execution belongs to */
  blockId: string;
  /** Execution mode — sl-1ahs.7.4: harmonised with backend ExecutionMode */
  mode: 'execute' | 'trigger';
  /** Start timestamp */
  startedAt: number;
  /** Completion timestamp */
  completedAt?: number;
  /**
   * Final status. `'awaiting_external'` is a derived value emitted by
   * the backend `ExecutionLogLongRunningOpEnricher` (sl-67kc.3.3) when
   * the log is still running AND at least one long-running operation is
   * parked (STT at Google, video transcode on the media runner, ...).
   * Terminal states never collapse to this value.
   */
  status:
    | 'running'
    | 'awaiting_external'
    | 'completed'
    | 'failed'
    | 'cancelled';
  /** Error message if status is 'failed' */
  error?: string;
  /** Stable safety classification; never inferred from free-form errors. */
  terminalReason?: 'cycle_blocked' | 'suspected_external_loop';

  /** INPUT stage data (what went into the pipeline) */
  input?: InputStageLog;
  /** PROCESS stage data (workflow execution) */
  process?: ProcessStageLog;
  /** OUTPUT stage data (write results) */
  output?: OutputStageLog;

  /**
   * sl-67kc.3.3: Long-running operations attached by the backend
   * `ExecutionLogLongRunningOpEnricher`. Empty when the execution has
   * never suspended — the common path. Populated for managed STT batch
   * runs while at least one row is still parked, and persists in the
   * `cancelled`/`failed`/`timeout`/`completed` terminal state until the
   * suspension row is purged so the History panel can render "what
   * happened" without an extra fetch.
   */
  longRunningOps?: LongRunningOpInfo[];
}

/** Maximum number of logs to keep per block */
const MAX_HISTORY_SIZE = 20;

type SocketExecutionStage = 'input' | 'process' | 'output';

interface SocketExecutionStagePayload {
  status?: string;
  startedAt?: string | Date;
  completedAt?: string | Date;
  data?: Record<string, unknown>;
  error?: string;
}

// =============================================================================
// SERVICE
// =============================================================================

export class LoggingService extends Service {
  /**
   * All logs per block (blockId -> logs array)
   */
  private readonly logs = new Map<string, LiveData<ExecutionLog[]>>();

  /**
   * Currently active log per block (for live updates during execution)
   */
  private readonly activeLogs = new Map<
    string,
    LiveData<ExecutionLog | null>
  >();

  // ===========================================================================
  // Reactive Getters
  // ===========================================================================

  /**
   * Get reactive logs for a block
   */
  getLogs$(blockId: string): LiveData<ExecutionLog[]> {
    let logs$ = this.logs.get(blockId);
    if (!logs$) {
      logs$ = new LiveData<ExecutionLog[]>([]);
      this.logs.set(blockId, logs$);
    }
    return logs$;
  }

  /**
   * Get reactive active log for a block (currently running execution)
   */
  getActiveLog$(blockId: string): LiveData<ExecutionLog | null> {
    let activeLog$ = this.activeLogs.get(blockId);
    if (!activeLog$) {
      activeLog$ = new LiveData<ExecutionLog | null>(null);
      this.activeLogs.set(blockId, activeLog$);
    }
    return activeLog$;
  }

  /**
   * Get current active log value (sync)
   */
  getActiveLog(blockId: string): ExecutionLog | null {
    return this.getActiveLog$(blockId).value;
  }

  // ===========================================================================
  // Lifecycle Methods
  // ===========================================================================

  /**
   * Start a new execution log
   */
  startLog(blockId: string, mode: 'execute' | 'trigger'): string {
    const logId = `log-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const log: ExecutionLog = {
      id: logId,
      blockId,
      mode,
      startedAt: Date.now(),
      status: 'running',
    };

    // Set as active log
    const activeLog$ = this.getActiveLog$(blockId);
    activeLog$.next(log);

    return logId;
  }

  /**
   * Complete the current execution log
   */
  completeLog(
    blockId: string,
    status: 'completed' | 'failed' | 'cancelled',
    error?: string
  ): void {
    const activeLog$ = this.getActiveLog$(blockId);
    const log = activeLog$.value;
    if (!log) return;

    const completedLog: ExecutionLog = {
      ...log,
      status,
      completedAt: Date.now(),
      ...(error && { error }),
    };

    // Add to history
    const logs$ = this.getLogs$(blockId);
    const currentLogs = logs$.value;
    const newLogs = [
      completedLog,
      ...currentLogs.filter(existing => existing.id !== completedLog.id),
    ].slice(0, MAX_HISTORY_SIZE);
    logs$.next(newLogs);

    // Clear active log
    activeLog$.next(null);
  }

  // ===========================================================================
  // Stage Update Methods
  // ===========================================================================

  /**
   * Log INPUT stage data
   */
  logInputStage(blockId: string, input: InputStageLog): void {
    const activeLog$ = this.getActiveLog$(blockId);
    const log = activeLog$.value;
    if (!log) return;

    activeLog$.next({
      ...log,
      input,
    });
  }

  /**
   * Log PROCESS stage data
   */
  logProcessStage(blockId: string, process: ProcessStageLog): void {
    const activeLog$ = this.getActiveLog$(blockId);
    const log = activeLog$.value;
    if (!log) return;

    activeLog$.next({
      ...log,
      process,
    });
  }

  /**
   * Log OUTPUT stage data
   */
  logOutputStage(blockId: string, output: OutputStageLog): void {
    const activeLog$ = this.getActiveLog$(blockId);
    const log = activeLog$.value;
    if (!log) return;

    activeLog$.next({
      ...log,
      output,
    });
  }

  /**
   * Apply runner progress to the active log so History can show the current
   * step while the backend log is still `running` and has no terminal
   * `process_stage.nodeSteps` yet.
   */
  applyExecutionProgress(
    blockId: string,
    progress: {
      logId: string;
      mode: 'execute' | 'trigger';
      timestamp: number;
      stepId: string;
      stepName: string;
      stepType: string;
      executionId: string;
      phase: 'start' | 'progress' | 'complete';
      activity?: string;
      message?: string;
      percent?: number;
      currentTimeMs?: number;
      totalTimeMs?: number;
      timeRemainingMs?: number;
      bytesDone?: number;
      bytesTotal?: number;
      bitrate?: string;
      fps?: number;
      detail?: Record<string, string | number | boolean>;
    }
  ): void {
    const activeLog$ = this.getActiveLog$(blockId);
    const existingActive = activeLog$.value;
    const existingBackend = this.getLogs$(blockId).value.find(
      log => log.id === progress.logId
    );
    const baseLog =
      existingActive?.id === progress.logId
        ? existingActive
        : (existingBackend ?? {
            id: progress.logId,
            blockId,
            mode: progress.mode,
            startedAt: progress.timestamp,
            status: 'running' as const,
          });

    if (baseLog.status !== 'running') {
      return;
    }

    const existingProcess = baseLog.process;
    const existingSteps = existingProcess?.nodeSteps ?? [];
    const stepStatus = progress.phase === 'complete' ? 'success' : 'running';
    const mediaProgress = {
      timestamp: progress.timestamp,
      stepId: progress.stepId,
      stepName: progress.stepName,
      stepType: progress.stepType,
      executionId: progress.executionId,
      phase: progress.phase,
      activity: progress.activity,
      message: progress.message,
      percent: progress.percent,
      currentTimeMs: progress.currentTimeMs,
      totalTimeMs: progress.totalTimeMs,
      timeRemainingMs: progress.timeRemainingMs,
      bytesDone: progress.bytesDone,
      bytesTotal: progress.bytesTotal,
      bitrate: progress.bitrate,
      fps: progress.fps,
      detail: progress.detail,
      lastUpdatedAt: progress.timestamp,
    };
    const matchedStepIndex = this.findMatchingNodeStepIndex(existingSteps, {
      stepId: progress.stepId,
      stepName: progress.stepName,
    });
    const matchedStep =
      matchedStepIndex >= 0 ? existingSteps[matchedStepIndex] : undefined;
    const currentStep: WorkflowNodeStep = {
      name: matchedStep?.name ?? progress.stepName,
      nodeId: matchedStep?.nodeId ?? progress.stepId,
      status: stepStatus,
      duration: matchedStep?.duration ?? 0,
      mediaProgress,
    };
    const nodeSteps =
      matchedStepIndex >= 0
        ? existingSteps.map(step =>
            step === matchedStep ? { ...step, ...currentStep } : step
          )
        : [...existingSteps, currentStep];
    const runtimeEvent: RuntimeProgressEvent = {
      timestamp: progress.timestamp,
      stepId: progress.stepId,
      stepName: progress.stepName,
      stepType: progress.stepType,
      executionId: progress.executionId,
      phase: progress.phase,
      activity: progress.activity,
      message: progress.message,
      percent: progress.percent,
      currentTimeMs: progress.currentTimeMs,
      totalTimeMs: progress.totalTimeMs,
      timeRemainingMs: progress.timeRemainingMs,
      bytesDone: progress.bytesDone,
      bytesTotal: progress.bytesTotal,
      bitrate: progress.bitrate,
      fps: progress.fps,
      detail: progress.detail,
    };
    const previousEvents = existingProcess?.runtimeEvents ?? [];
    const previousEvent = previousEvents[previousEvents.length - 1];
    const runtimeEvents =
      previousEvent &&
      previousEvent.stepId === runtimeEvent.stepId &&
      previousEvent.phase === runtimeEvent.phase &&
      previousEvent.activity === runtimeEvent.activity &&
      previousEvent.message === runtimeEvent.message &&
      previousEvent.percent === runtimeEvent.percent &&
      previousEvent.bytesDone === runtimeEvent.bytesDone &&
      previousEvent.bytesTotal === runtimeEvent.bytesTotal
        ? previousEvents
        : [...previousEvents, runtimeEvent].slice(-50);

    activeLog$.next({
      ...baseLog,
      mode: progress.mode,
      status: 'running',
      process: {
        ...existingProcess,
        duration: Math.max(
          existingProcess?.duration ?? 0,
          Math.max(0, progress.timestamp - baseLog.startedAt)
        ),
        status: 'running',
        nodeSteps,
        runtimeEvents,
        mediaProgress,
      },
    });
  }

  private findMatchingNodeStepIndex(
    steps: WorkflowNodeStep[],
    progress: { stepId: string; stepName: string }
  ): number {
    const byId = steps.findIndex(step => step.nodeId === progress.stepId);
    if (byId >= 0) return byId;

    const progressName = this.normalizeStepName(progress.stepName);
    if (!progressName) return -1;

    return steps.findIndex(
      step => this.normalizeStepName(step.name) === progressName
    );
  }

  private normalizeStepName(value: string | undefined): string {
    return value?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';
  }

  /**
   * Apply a live Socket.IO stage event to the active log so the UI updates
   * immediately instead of waiting for the follow-up REST refresh.
   */
  applySocketStage(
    blockId: string,
    logId: string,
    mode: 'execute' | 'trigger',
    stage: SocketExecutionStage,
    stageData: SocketExecutionStagePayload,
    timestamp: number
  ): void {
    const activeLog$ = this.getActiveLog$(blockId);
    const startedAt =
      this.toTimestamp(stageData.startedAt) ??
      activeLog$.value?.startedAt ??
      timestamp;

    const currentLog =
      activeLog$.value?.id === logId
        ? activeLog$.value
        : ({
            id: logId,
            blockId,
            mode,
            startedAt,
            status: 'running',
          } satisfies ExecutionLog);

    const nextLog: ExecutionLog = {
      ...currentLog,
      mode,
      startedAt,
      status: 'running',
    };

    if (stage === 'input') {
      nextLog.input = this.buildInputStageFromSocket(stageData);
    } else if (stage === 'process') {
      nextLog.process = this.buildProcessStageFromSocket(stageData);
    } else {
      nextLog.output = this.buildOutputStageFromSocket(stageData);
    }

    activeLog$.next(nextLog);
  }

  // ===========================================================================
  // Query Methods
  // ===========================================================================

  /**
   * Get a specific log by ID
   */
  getLog(blockId: string, logId: string): ExecutionLog | undefined {
    const logs = this.getLogs$(blockId).value;
    return logs.find((l: ExecutionLog) => l.id === logId);
  }

  /**
   * Get the most recent log for a block
   */
  getLatestLog(blockId: string): ExecutionLog | undefined {
    const logs = this.getLogs$(blockId).value;
    return logs[0];
  }

  /**
   * Get execution statistics for a block
   */
  getStats(blockId: string): {
    totalExecutions: number;
    totalRowsProcessed: number;
    successRate: number;
    lastExecution?: number;
  } {
    const logs = this.getLogs$(blockId).value;

    if (logs.length === 0) {
      return {
        totalExecutions: 0,
        totalRowsProcessed: 0,
        successRate: 0,
      };
    }

    const completedLogs = logs.filter(
      (l: ExecutionLog) => l.status === 'completed'
    );
    const totalRowsProcessed = logs.reduce(
      (sum: number, l: ExecutionLog) => sum + (l.input?.passedFilter ?? 0),
      0
    );

    return {
      totalExecutions: logs.length,
      totalRowsProcessed,
      successRate:
        logs.length > 0
          ? Math.round((completedLogs.length / logs.length) * 100)
          : 0,
      lastExecution: logs[0]?.startedAt,
    };
  }

  // ===========================================================================
  // Cleanup Methods
  // ===========================================================================

  /**
   * Delete a specific log
   */
  deleteLog(blockId: string, logId: string): void {
    const logs$ = this.getLogs$(blockId);
    const currentLogs = logs$.value;
    logs$.next(currentLogs.filter((l: ExecutionLog) => l.id !== logId));
  }

  /**
   * Clear all logs for a block
   */
  clearLogs(blockId: string): void {
    const logs$ = this.getLogs$(blockId);
    logs$.next([]);
  }

  /**
   * Cleanup when block is deleted
   */
  deleteBlockLogs(blockId: string): void {
    this.logs.delete(blockId);
    this.activeLogs.delete(blockId);
  }

  // ===========================================================================
  // Backend Integration (E002-T046)
  // ===========================================================================

  /** Track which blocks have loaded from backend */
  private readonly loadedFromBackend = new Set<string>();

  /** Loading state per block */
  private readonly loadingState = new Map<string, LiveData<boolean>>();

  /**
   * Get loading state for a block
   */
  getLoadingState$(blockId: string): LiveData<boolean> {
    let loading$ = this.loadingState.get(blockId);
    if (!loading$) {
      loading$ = new LiveData<boolean>(false);
      this.loadingState.set(blockId, loading$);
    }
    return loading$;
  }

  private request(
    workspaceId: string,
    path: string,
    init: RequestInit = {}
  ): Promise<Response> {
    const method = init.method ?? 'GET';
    const isMutation = method !== 'GET';
    return defaultWorkspaceApiClient.request({
      workspaceId,
      path,
      init,
      category: 'workspace-api',
      key: `${method}:${path}`,
      mode: isMutation ? 'foreground' : 'background',
      priority: isMutation ? 'foreground' : 'visible',
    });
  }

  /**
   * Load logs from backend API and merge with local logs.
   * Only loads once per block (unless forced).
   *
   * `workspaceId` is required — the backend enforces tenant isolation and
   * returns 400 without it.
   */
  async loadFromBackend(
    blockId: string,
    workspaceId: string,
    force = false
  ): Promise<void> {
    // Skip if already loaded (unless forced)
    if (this.loadedFromBackend.has(blockId) && !force) {
      return;
    }

    const loading$ = this.getLoadingState$(blockId);
    loading$.next(true);

    try {
      const response = await this.request(
        workspaceId,
        workflowWorkspaceRoutes.blockLogs(workspaceId, blockId, { limit: 50 })
      );
      if (!response.ok) {
        console.warn(`Failed to load logs from backend: ${response.status}`);
        return;
      }

      const data = (await response.json()) as { logs: BackendLogResponse[] };
      const backendLogs = data.logs || [];

      // Convert backend logs to frontend format
      const convertedLogs = backendLogs.map(log => this.convertBackendLog(log));

      // Merge with existing in-memory logs
      const logs$ = this.getLogs$(blockId);
      const existingLogs = logs$.value;

      // Merge: backend logs take priority over in-memory logs
      // This ensures "completed" status from backend overrides stale "running" status
      const mergedLogsMap = new Map<string, ExecutionLog>();

      // First add existing logs
      for (const log of existingLogs) {
        mergedLogsMap.set(log.id, log);
      }

      // Then override with backend logs (they have the authoritative status)
      for (const log of convertedLogs) {
        mergedLogsMap.set(log.id, log);
      }

      // Convert to array, sort by startedAt (newest first), and limit
      const mergedLogs = Array.from(mergedLogsMap.values())
        .sort((a, b) => b.startedAt - a.startedAt)
        .slice(0, MAX_HISTORY_SIZE);

      logs$.next(mergedLogs);
      this.loadedFromBackend.add(blockId);
    } catch (error) {
      console.error('Failed to load logs from backend:', error);
    } finally {
      loading$.next(false);
    }
  }

  /**
   * Add a single log from backend (e.g., from Socket.IO event)
   */
  addBackendLog(blockId: string, backendLog: BackendLogResponse): void {
    const log = this.convertBackendLog(backendLog);
    const logs$ = this.getLogs$(blockId);
    const currentLogs = logs$.value;

    // Check if log already exists (update it) or add new
    const existingIndex = currentLogs.findIndex(l => l.id === log.id);

    if (existingIndex >= 0) {
      // Update existing log
      const newLogs = [...currentLogs];
      newLogs[existingIndex] = log;
      logs$.next(newLogs);
    } else {
      // Add new log at the beginning
      const newLogs = [log, ...currentLogs].slice(0, MAX_HISTORY_SIZE);
      logs$.next(newLogs);
    }
  }

  /**
   * Convert backend log format to frontend ExecutionLog format
   */
  private convertBackendLog(backendLog: BackendLogResponse): ExecutionLog {
    const log: ExecutionLog = {
      id: backendLog.id,
      blockId: backendLog.blockId,
      mode: backendLog.mode as 'execute' | 'trigger',
      startedAt: new Date(backendLog.startedAt).getTime(),
      completedAt: backendLog.completedAt
        ? new Date(backendLog.completedAt).getTime()
        : undefined,
      status: backendLog.status as ExecutionLog['status'],
      // Backend sanitizer emits `{step, message}` — unpack to a string for
      // the UI's `<ErrorBox message={log.error}/>`. Passing the object
      // crashes React ("Objects are not valid as a React child").
      error: backendLog.error?.message || undefined,
      terminalReason: backendLog.terminalReason,
    };

    // Sanitized format (sl-3je7): Backend now returns engine-agnostic data
    const sanitized = backendLog as unknown as {
      input?: {
        sourceTable: string;
        totalRows: number;
        passedFilter: number;
        filteredOut: number;
        payloads: Array<Record<string, unknown>>;
      };
      process?: {
        status: 'success' | 'error' | 'running' | 'queued';
        duration: number;
        response?: Record<string, unknown>;
      };
      steps?: Array<{
        name: string;
        nodeId?: string;
        status: 'success' | 'error' | 'running';
        duration: number;
        inputSummary?: string[];
        outputSummary?: string[];
        inputData?: Array<Record<string, unknown>>;
        outputData?: Array<Record<string, unknown>>;
        error?: string;
        // sl-oe5x audit M1: typed runner error forwarded from SanitizedStep
        errorCode?: string;
        errorDetails?: Record<string, unknown>;
      }>;
      output?: {
        mode: string;
        targetTable?: string;
        rowsWritten: number;
        rowsFailed: number;
        error?: string;
      };
      error?: { step: string; message: string };
      duration?: number;
      runtimeEvents?: RuntimeProgressEvent[];
      mediaProgress?: ProcessStageLog['mediaProgress'];
      // sl-67kc.3.3: long-running ops attached by the backend enricher.
      // The wire shape mirrors the LongRunningOpInfo interface above.
      longRunningOps?: LongRunningOpInfo[];
    };

    // sl-67kc.3.3: pass through long-running ops verbatim — the backend
    // already shaped them as ISO timestamps + cherry-picked sttJob fields.
    if (sanitized.longRunningOps && sanitized.longRunningOps.length > 0) {
      log.longRunningOps = sanitized.longRunningOps;
    }

    // Input stage — sanitized format only. Pre-sanitized logs have no
    // structured input data.
    if (sanitized.input) {
      log.input = {
        sourceTable: sanitized.input.sourceTable,
        sourceTableId: '',
        totalRows: sanitized.input.totalRows,
        passedFilter: sanitized.input.passedFilter,
        filteredOut: sanitized.input.filteredOut,
        payloads: sanitized.input.payloads,
      };
    }

    // Process stage — sanitized format only.
    if (
      sanitized.process ||
      sanitized.steps ||
      sanitized.mediaProgress ||
      sanitized.runtimeEvents
    ) {
      const terminalStatus = backendLog.status !== 'running';
      const normalizedSteps = sanitized.steps?.map(step => {
        const status =
          step.status === 'running' && terminalStatus
            ? backendLog.status === 'failed'
              ? 'error'
              : 'success'
            : step.status;
        return {
          name: step.name,
          nodeId: step.nodeId,
          status,
          duration: step.duration,
          inputSummary: step.inputSummary,
          outputSummary: step.outputSummary,
          // sl-j7kp.3.1: Forward sanitized step I/O data from backend.
          inputData: step.inputData,
          outputData: step.outputData,
          error:
            status === 'error'
              ? (step.error ?? sanitized.error?.message)
              : step.error,
          // sl-oe5x audit M1: forward structured error code + details so
          // the history panel can resolve a localised message instead of
          // rendering the runner's raw `error` string for plan-gate
          // rejections.
          errorCode: step.errorCode,
          errorDetails: step.errorDetails,
        };
      });
      const failedStep = normalizedSteps?.find(s => s.status === 'error');
      log.process = {
        duration: sanitized.process?.duration ?? sanitized.duration ?? 0,
        status:
          sanitized.process?.status ??
          (backendLog.status === 'failed' || failedStep
            ? 'error'
            : backendLog.status === 'running'
              ? 'running'
              : 'success'),
        nodeSteps: normalizedSteps,
        error: sanitized.error?.message,
        errorStep: sanitized.error?.step,
        response: sanitized.process?.response,
        runtimeEvents: sanitized.runtimeEvents,
        mediaProgress: sanitized.mediaProgress,
      };
    }

    // Output stage — sanitized format only.
    if (sanitized.output) {
      log.output = {
        mode: (sanitized.output.mode ||
          'not_configured') as OutputStageLog['mode'],
        targetTable: sanitized.output.targetTable,
        rowsWritten: sanitized.output.rowsWritten ?? 0,
        rowsFailed: sanitized.output.rowsFailed ?? 0,
        error: sanitized.output.error,
        results: [],
      };
    }

    return log;
  }

  /**
   * Refresh logs from backend (force reload)
   */
  async refreshFromBackend(
    blockId: string,
    workspaceId: string
  ): Promise<void> {
    return this.loadFromBackend(blockId, workspaceId, true);
  }

  /**
   * Alias for refreshFromBackend - used after backend execution
   */
  refreshLogs(blockId: string, workspaceId: string): void {
    this.refreshFromBackend(blockId, workspaceId).catch(error => {
      console.error('Failed to refresh logs:', error);
    });
  }

  private buildInputStageFromSocket(
    stageData: SocketExecutionStagePayload
  ): InputStageLog {
    const data = this.asRecord(stageData.data);
    const sampleRows = this.asRecordArray(data.sampleRows);
    const rowCount = this.toNumber(data.rowCount);

    return {
      sourceTable:
        this.toStringOrUndefined(data.sourceTableName) ??
        this.toStringOrUndefined(data.sourceTableId) ??
        'Unknown',
      sourceTableId: this.toStringOrUndefined(data.sourceTableId) ?? '',
      totalRows: rowCount,
      passedFilter: rowCount,
      filteredOut: 0,
      payloads: sampleRows,
    };
  }

  private buildProcessStageFromSocket(
    stageData: SocketExecutionStagePayload
  ): ProcessStageLog {
    const data = this.asRecord(stageData.data ?? stageData);
    const responseData = data.responseData;
    const nodeSteps = this.asNodeStepArray(data.nodeSteps ?? data.steps);
    const hasRunningStep = Boolean(
      nodeSteps?.some(step => step.status === 'running')
    );
    const responseStatus = this.toNumber(data.responseStatus);
    const responseDataStatus =
      responseData && typeof responseData === 'object'
        ? this.toStringOrUndefined(
            (responseData as Record<string, unknown>).status
          )
        : undefined;

    return {
      duration: this.toNumber(data.duration ?? data.totalDurationMs),
      status:
        responseDataStatus === 'queued'
          ? 'queued'
          : stageData.status === 'failed'
            ? 'error'
            : stageData.status === 'running' ||
                responseStatus === 102 ||
                hasRunningStep
              ? 'running'
              : 'success',
      nodeSteps,
      error: stageData.error,
      response:
        responseData && typeof responseData === 'object'
          ? (responseData as Record<string, unknown>)
          : undefined,
      executionId: this.toStringOrUndefined(data.executionId),
      workflowName: this.toStringOrUndefined(data.workflowName),
      rows: this.asProcessRows(data.rows),
      runtimeEvents: this.asRuntimeProgressEvents(data.runtimeEvents),
      mediaProgress: this.asRuntimeProgressEvent(data.mediaProgress),
    };
  }

  private buildOutputStageFromSocket(
    stageData: SocketExecutionStagePayload
  ): OutputStageLog {
    const data = this.asRecord(stageData.data);
    const outputTables = this.asRecordArray(data.outputTables);
    const primaryTarget = outputTables[0];

    return {
      mode: this.toOutputMode(data.outputMode),
      targetTable: this.toStringOrUndefined(primaryTarget?.tableName),
      targetTableId: this.toStringOrUndefined(primaryTarget?.tableId),
      rowsWritten: this.toNumber(data.rowsWritten),
      rowsFailed: this.toNumber(data.rowsFailed),
      error: stageData.error,
      outputMappings: this.asOutputMappings(data.mappings),
      results: this.asOutputResults(data.perRowResults),
    };
  }

  private toTimestamp(value: string | Date | undefined): number | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value.getTime();
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? undefined : timestamp;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  }

  private asRecordArray(value: unknown): Array<Record<string, unknown>> {
    if (!Array.isArray(value)) return [];
    return value
      .filter(item => item && typeof item === 'object')
      .map(item => item as Record<string, unknown>);
  }

  private asNodeStepArray(value: unknown): WorkflowNodeStep[] | undefined {
    if (!Array.isArray(value)) return undefined;

    const steps = value
      .filter(item => item && typeof item === 'object')
      .map(item => {
        const step = item as Record<string, unknown>;
        const rawStatus = step.status;
        return {
          name:
            this.toStringOrUndefined(step.nodeName) ??
            this.toStringOrUndefined(step.name) ??
            'Unknown',
          nodeId:
            this.toStringOrUndefined(step.nodeId) ??
            this.toStringOrUndefined(step.id),
          status:
            rawStatus === 'running'
              ? 'running'
              : rawStatus === 'error' ||
                  rawStatus === 'timeout' ||
                  rawStatus === 'failed'
                ? 'error'
                : 'success',
          duration: this.toNumber(step.duration ?? step.durationMs),
          inputSummary:
            typeof step.inputSummary === 'string'
              ? [step.inputSummary]
              : Array.isArray(step.inputSummary)
                ? step.inputSummary.filter(
                    (value): value is string => typeof value === 'string'
                  )
                : undefined,
          outputSummary:
            typeof step.outputSummary === 'string'
              ? [step.outputSummary]
              : Array.isArray(step.outputSummary)
                ? step.outputSummary.filter(
                    (value): value is string => typeof value === 'string'
                  )
                : undefined,
          inputData: this.asRecordArray(step.inputItems),
          outputData: Array.isArray(step.outputItems)
            ? this.asRecordArray(step.outputItems)
            : undefined,
          error: this.toStringOrUndefined(step.error),
          errorCode: this.toStringOrUndefined(step.errorCode),
          errorDetails:
            step.errorDetails && typeof step.errorDetails === 'object'
              ? (step.errorDetails as Record<string, unknown>)
              : undefined,
        } satisfies WorkflowNodeStep;
      });

    return steps.length > 0 ? steps : undefined;
  }

  private asProcessRows(value: unknown): ProcessStageRowEntry[] | undefined {
    if (!Array.isArray(value)) return undefined;

    const rows = value
      .filter(item => item && typeof item === 'object')
      .map(item => {
        const row = item as Record<string, unknown>;
        const status = row.status;
        return {
          rowId: this.toStringOrUndefined(row.rowId) ?? '',
          status:
            status === 'success' ||
            status === 'failed' ||
            status === 'aborted' ||
            status === 'abandoned'
              ? status
              : 'failed',
          retryCount: this.toNumber(row.retryCount),
          onFailureMode: row.onFailureMode === 'continue' ? 'continue' : 'stop',
          error: this.toStringOrUndefined(row.error),
          durationMs: this.toNumber(row.durationMs),
        } satisfies ProcessStageRowEntry;
      })
      .filter(row => row.rowId.length > 0);

    return rows.length > 0 ? rows : undefined;
  }

  private asRuntimeProgressEvents(
    value: unknown
  ): RuntimeProgressEvent[] | undefined {
    if (!Array.isArray(value)) return undefined;

    const events = value
      .slice(-50)
      .map(item => this.asRuntimeProgressEvent(item))
      .filter((event): event is RuntimeProgressEvent => Boolean(event));

    return events.length > 0 ? events : undefined;
  }

  private asRuntimeProgressEvent(
    value: unknown
  ): RuntimeProgressEvent | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const item = value as Record<string, unknown>;
    const timestamp = this.toOptionalNumber(item.timestamp);
    if (timestamp === undefined) return undefined;

    return {
      timestamp,
      stepId: this.toStringOrUndefined(item.stepId),
      stepName: this.toStringOrUndefined(item.stepName),
      stepType: this.toStringOrUndefined(item.stepType),
      executionId: this.toStringOrUndefined(item.executionId),
      phase:
        item.phase === 'start' ||
        item.phase === 'progress' ||
        item.phase === 'complete'
          ? item.phase
          : undefined,
      activity: this.toStringOrUndefined(item.activity),
      message: this.toStringOrUndefined(item.message),
      percent: this.toOptionalNumber(item.percent),
      currentTimeMs: this.toOptionalNumber(item.currentTimeMs),
      totalTimeMs: this.toOptionalNumber(item.totalTimeMs),
      timeRemainingMs: this.toOptionalNumber(item.timeRemainingMs),
      bytesDone: this.toOptionalNumber(item.bytesDone),
      bytesTotal: this.toOptionalNumber(item.bytesTotal),
      bitrate: this.toStringOrUndefined(item.bitrate),
      fps: this.toOptionalNumber(item.fps),
      detail: this.asProgressDetail(item.detail),
    };
  }

  private asProgressDetail(
    value: unknown
  ): Record<string, string | number | boolean> | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const result: Record<string, string | number | boolean> = {};
    for (const [key, detailValue] of Object.entries(
      value as Record<string, unknown>
    ).slice(0, 8)) {
      if (this.isProgressDetailValue(detailValue)) {
        result[key] = detailValue;
      }
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  private isProgressDetailValue(
    value: unknown
  ): value is string | number | boolean {
    return (
      typeof value === 'string' ||
      typeof value === 'boolean' ||
      (typeof value === 'number' && Number.isFinite(value))
    );
  }

  private asOutputMappings(
    value: unknown
  ): OutputStageLog['outputMappings'] | undefined {
    if (!Array.isArray(value)) return undefined;

    const mappings = value
      .filter(item => item && typeof item === 'object')
      .map(item => {
        const mapping = item as Record<string, unknown>;
        return {
          responseField: this.toStringOrUndefined(mapping.sourceField) ?? '',
          targetColumn: this.toStringOrUndefined(mapping.targetColumn) ?? '',
          transform: this.toStringOrUndefined(mapping.transform),
        };
      })
      .filter(
        mapping =>
          mapping.responseField.length > 0 || mapping.targetColumn.length > 0
      );

    return mappings.length > 0 ? mappings : undefined;
  }

  private asOutputResults(value: unknown): OutputStageLog['results'] {
    if (!Array.isArray(value)) return [];

    return value
      .filter(item => item && typeof item === 'object')
      .map(item => {
        const result = item as Record<string, unknown>;
        return {
          rowId: this.toStringOrUndefined(result.rowId) ?? '',
          success: Boolean(result.success),
          error: this.toStringOrUndefined(result.error),
        };
      })
      .filter(result => result.rowId.length > 0);
  }

  private toStringOrUndefined(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined;
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toOptionalNumber(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value)
      ? value
      : undefined;
  }

  private toOutputMode(value: unknown): OutputStageLog['mode'] {
    switch (value) {
      case 'update_source':
      case 'create_table':
      case 'append_existing':
      case 'update_target':
        return value;
      default:
        return 'not_configured';
    }
  }
}
